// Runs a VRM avatar's face, eyes, spring bones and first-person view. Mounted
// by FixedRetargetedAvatar (local and remote players) next to a VRM model; the
// body itself is animated by FixedRetargetedAvatar's mixer like any avatar.
import React, { useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { VRM } from '@pixiv/three-vrm';
import { VrmFace, VrmFirstPersonView } from '../../avatar/vrm/vrmRuntime';
import { avatarVoiceLevel, avatarVoiceLevelForUser } from '../../services/avatarVoice';

/** Dev/test hook: window.__gxVoiceSim = { local: 0.3, '<userId>': 0.5, '*': 0.2 }. */
function simulatedVoice(key: string): number | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  const sim = (window as any).__gxVoiceSim;
  if (!sim) return null;
  const v = sim[key] ?? sim['*'];
  return typeof v === 'number' ? v : null;
}

const _cam = new THREE.Vector3();
const _head = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _q = new THREE.Quaternion();

interface Props {
  vrm: VRM;
  isRemote: boolean;
  /** Remote player's Supabase user id (matches their LiveKit identity). */
  voiceUserId?: string | null;
  /** Local desktop first-person camera: hide our own head. */
  firstPerson?: boolean;
  /** Remote LOD: 'frozen' / 'billboard' skip face + springs. */
  lodRef?: React.MutableRefObject<{ lod?: 'full' | 'frozen' | 'billboard' } | undefined | null>;
  seed: string;
}

const VrmAvatarRuntime: React.FC<Props> = ({ vrm, isRemote, voiceUserId = null, firstPerson = false, lodRef, seed }) => {
  const { camera } = useThree();
  const face = useMemo(() => new VrmFace(vrm, seed), [vrm, seed]);
  const fp = useMemo(() => new VrmFirstPersonView(vrm), [vrm]);

  useEffect(() => { fp.setFirstPerson(!isRemote && firstPerson); }, [fp, firstPerson, isRemote]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as any;
    const reg = (w.__gxVrm ||= {});
    const key = isRemote ? `remote:${voiceUserId || seed}` : 'local';
    reg[key] = { vrm, face, fp };
    return () => { if (reg[key]?.vrm === vrm) delete reg[key]; };
  }, [vrm, face, fp, isRemote, voiceUserId, seed]);

  useFrame((_, delta) => {
    const lod = lodRef?.current?.lod ?? 'full';
    if (isRemote && lod !== 'full') return;

    const simKey = isRemote ? (voiceUserId || '') : 'local';
    const voice = simulatedVoice(simKey) ?? (isRemote ? avatarVoiceLevelForUser(voiceUserId) : avatarVoiceLevel());

    // Remote players glance at you when you're close and in front of them.
    let look: THREE.Vector3 | null = null;
    if (isRemote) {
      const head = vrm.humanoid.getRawBoneNode('head');
      if (head) {
        head.getWorldPosition(_head);
        camera.getWorldPosition(_cam);
        const d = _cam.distanceTo(_head);
        if (d < 4.5) {
          // The wrapper faces +Z (a VRM 0.x scene itself is turned 180° inside it).
          (vrm.scene.parent ?? vrm.scene).getWorldQuaternion(_q);
          _fwd.set(0, 0, 1).applyQuaternion(_q);
          if (_fwd.dot(_head.sub(_cam).negate().normalize()) > 0.3) look = _cam;
        }
      }
    }
    face.update(delta, voice, look);
  });

  return null;
};

export default VrmAvatarRuntime;
